'use client';

import Image from 'next/image';
import { useState, useRef, useEffect, useCallback } from 'react';

const LocalVideo = ({
    className,
    priorityAllImages,
    video,
    videoWebm,
    videoPosterImg,
    priorityVideo,
    priorityVideoPoster,
    priorityPersonalOnlyForDesktop
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const videoRef = useRef(null);
    const containerRef = useRef(null);

    const getVideoPriority = (specificPriority) => {
        if (priorityPersonalOnlyForDesktop) {
            return false;
        }
        return specificPriority !== undefined ? !!specificPriority : !!priorityAllImages;
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                const isVisible = entry.isIntersecting;
                setIsIntersecting(isVisible);

                if (isVisible && !shouldLoadVideo) {
                    const timer = setTimeout(() => {
                        setShouldLoadVideo(true);
                    }, 1000);

                    return () => clearTimeout(timer);
                }
            },
            {
                rootMargin: '50px',
                threshold: 0.1
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, [shouldLoadVideo]);

    useEffect(() => {
        const video = videoRef.current;
        return () => {
            if (video) {
                video.pause();
                video.src = '';
            }
        };
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isIntersecting || !shouldLoadVideo) return;

        const handlePlay = () => {
            setIsPlaying(true);
            setHasPlayedOnce(true);
        };
        const handlePause = () => setIsPlaying(false);
        const handleLoadedMetadata = () => {
            setIsPlaying(!video.paused);
            setVideoError(false);
        };

        const handleError = (e) => {
            console.warn('Video loading error:', e);
            setVideoError(true);
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);

        video.addEventListener('error', handleError);

        if (!hasPlayedOnce && video.paused && isIntersecting) {
            const hasUserInteracted =
                document.visibilityState === 'visible' &&
                (document.hasFocus() || navigator.userActivation?.hasBeenActive);

            if (hasUserInteracted) {
                video.play().catch((error) => {
                    console.warn('Auto-play failed:', error);
                });
            }
        }

        return () => {
            if (video) {
                video.removeEventListener('play', handlePlay);
                video.removeEventListener('pause', handlePause);
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);

                video.removeEventListener('error', handleError);
            }
        };
    }, [isIntersecting, hasPlayedOnce, shouldLoadVideo]);

    const handleVideoClick = useCallback(async () => {
        if (videoRef.current && !videoError) {
            try {
                if (isPlaying) {
                    videoRef.current.pause();
                } else {
                    if (!shouldLoadVideo) {
                        setShouldLoadVideo(true);
                    }

                    requestAnimationFrame(async () => {
                        try {
                            await videoRef.current.play();
                        } catch (playError) {
                            console.warn('Video playback failed:', playError);
                            setIsPlaying(false);
                        }
                    });
                }
            } catch (error) {
                console.warn('Video playback failed:', error);
                setIsPlaying(false);
            }
        }
    }, [isPlaying, videoError, shouldLoadVideo]);

    const handleMuteClick = useCallback(
        (e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
        },
        [isMuted]
    );

    if (videoError) {
        return (
            <div ref={containerRef} className={`${className} relative`}>
                <Image
                    src={
                        videoPosterImg
                            ? `/dA/${videoPosterImg}`
                            : '/assets/home/banner-03-mobile.png'
                    }
                    quality={55}
                    alt="dotCMS video fallback"
                    width={0}
                    height={0}
                    sizes="(max-width: 882px) calc(100vw - 32px), 882px"
                    className="size-full object-cover"
                    priority={getVideoPriority(priorityVideoPoster)}
                />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`${className} relative cursor-pointer`}
            onClick={handleVideoClick}>
            {shouldLoadVideo && (
                <video
                    ref={videoRef}
                    muted={isMuted}
                    loop
                    playsInline
                    preload="none"
                    poster={
                        videoPosterImg
                            ? `/dA/${videoPosterImg.identifier}/asset/${videoPosterImg.underlyingFileName}`
                            : '/assets/home/banner-03-mobile.png'
                    }
                    className="size-full object-cover"
                    style={{
                        willChange: 'auto'
                    }}>
                    {videoWebm && <source src={`/dA/${videoWebm}`} type="video/webm" />}
                    <source
                        src={video ? video.idPath : '/assets/video/dotCMS.mp4'}
                        type="video/mp4"
                    />
                    Your browser does not support the video tag.
                </video>
            )}

            <button
                onClick={handleMuteClick}
                className="absolute right-4 top-4 z-10 flex size-8 items-center justify-center rounded-full bg-white transition-colors hover:bg-fuschia-700"
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}>
                <Image
                    src={
                        isMuted
                            ? '/assets/icons/video/video-mute-on.svg'
                            : '/assets/icons/video/video-mute-off.svg'
                    }
                    alt={isMuted ? 'Muted' : 'Unmuted'}
                    unoptimized
                    width={16}
                    height={16}
                    priority={getVideoPriority(priorityVideo)}
                />
            </button>

            {(!isPlaying || !shouldLoadVideo) && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="size-16 rounded-full bg-white md:size-20 lg:size-[240px]">
                        <Image
                            src="/assets/icons/play-video-local.svg"
                            alt="Play video"
                            width={240}
                            height={240}
                            priority={getVideoPriority(priorityVideo)}
                            unoptimized
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

function BannerImages(props) {
    const {
        priorityAllImages,
        video,
        videoWebm,
        picTopLeft,
        picTopRight,
        picBottomLeft,
        picBottomRight,
        videoPosterImg,
        priorityTopLeft,
        priorityTopRight,
        priorityBottomLeft,
        priorityBottomRight,
        priorityVideo,
        priorityVideoPoster,
        priorityPersonalOnlyForDesktop
    } = props;
    const getPriority = (specificPriority) => {
        if (priorityPersonalOnlyForDesktop) {
            return false;
        }
        return specificPriority !== undefined ? !!specificPriority : !!priorityAllImages;
    };

    return (
        <>
            <div className="relative flex flex-col gap-8">
                <div className="flex justify-center gap-2 overflow-x-hidden md:gap-5 lg:gap-6">
                    <div className="hidden w-[23.90875rem] flex-col items-end justify-end md:flex md:w-[46.75rem] md:shrink-0 md:gap-5 lg:w-[55.125rem] lg:justify-start lg:gap-6">
                        <div className="h-[4.25rem] md:h-34 lg:h-[16.5rem]">
                            <Image
                                src={
                                    picTopLeft && Object.keys(picTopLeft).length > 0
                                        ? `/dA/${picTopLeft}`
                                        : `/assets/home/banner-01.svg`
                                }
                                alt="dotCMS dashboard interface screenshot"
                                width={0}
                                height={0}
                                sizes="(max-width: 452px) calc(100vw - 32px), 452px"
                                className="size-full lg:block"
                                priority={getPriority(priorityTopLeft)}
                            />
                        </div>
                        <div className="relative h-[4.25rem] md:h-[8.25rem] lg:h-[13.5rem]">
                            <Image
                                src={
                                    picBottomLeft && Object.keys(picBottomLeft).length > 0
                                        ? `/dA/${picBottomLeft}`
                                        : `/assets/home/banner-02.webp`
                                }
                                alt="dotCMS content management features screenshot"
                                width={0}
                                height={0}
                                sizes="259px"
                                quality={55}
                                className="size-full object-contain lg:block"
                                priority={getPriority(priorityBottomLeft)}
                            />
                        </div>
                    </div>

                    <div className="flex w-full flex-col items-end justify-start md:w-[46.75rem] md:shrink-0 md:gap-5 lg:w-[55.125rem] lg:gap-6">
                        <div className="size-full min-h-34 md:min-h-[17rem] lg:min-h-[30rem]">
                            <LocalVideo
                                video={video}
                                videoPosterImg={videoPosterImg}
                                videoWebm={videoWebm}
                                priorityAllImages={!!priorityAllImages}
                                priorityVideo={priorityVideo}
                                priorityVideoPoster={priorityVideoPoster}
                                priorityPersonalOnlyForDesktop={priorityPersonalOnlyForDesktop}
                                className="size-full"
                            />
                        </div>
                    </div>

                    <div className="hidden w-[23.90875rem] flex-col items-start justify-end md:flex md:w-[46.75rem] md:shrink-0 md:gap-5 lg:relative lg:w-[55.125rem] lg:justify-start lg:gap-6">
                        <div className="h-28 md:h-[13.5rem] lg:h-[22rem]">
                            <Image
                                src={
                                    picTopRight && Object.keys(picTopRight).length > 0
                                        ? `/dA/${picTopRight}`
                                        : `/assets/home/banner-04.webp`
                                }
                                alt="dotCMS editing interface screenshot"
                                width={0}
                                height={0}
                                sizes="(max-width: 577px) calc(100vw - 32px), 577px"
                                quality={55}
                                className="size-full object-contain lg:block"
                                priority={getPriority(priorityTopRight)}
                            />
                        </div>
                        <div className="relative h-5 md:h-[3.75rem] lg:h-[10.75rem]">
                            <Image
                                src={
                                    picBottomRight && Object.keys(picBottomRight).length > 0
                                        ? `/dA/${picBottomRight}`
                                        : `/assets/home/banner-05.svg`
                                }
                                alt="dotCMS workflow and collaboration tools screenshot"
                                width={0}
                                height={0}
                                sizes="215px"
                                className="block size-full object-contain"
                                priority={getPriority(priorityBottomRight)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BannerImages;
